import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import html2canvas from 'html2canvas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link } from 'react-router-dom';
import { INDUSTRY_INTELLIGENCE } from '@/lib/industryIntelligence';
import {
  Lock, Loader2, ArrowRight, Lightbulb, CircleDot, ChevronRight,
} from 'lucide-react';
import { LOGO_URL } from '@/lib/content/landingContent';
import {
  SEVERITY_STYLES, CONFIDENCE_STYLES, PRIORITY_STYLES, TOUCHPOINT_META,
  SECTION_LABELS, SNAPSHOT_CONTENT, LEAD_GATE, LEAD_FORM_FIELDS,
  RESULTS_CTA, DOWNLOAD_SUMMARY, CONFIRMATION, SCORE_BAND,
  SUMMARY_CARDS, WATERMARK,
} from '@/lib/content/resultContent';

// ──────────────────────────────────────────────────────────
// SHARED UI
// ──────────────────────────────────────────────────────────

function SectionLabel({ icon: Icon, label }) {
  return (
    <h4 className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-3 flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" /> {label}
    </h4>
  );
}

function GatedPreviewCard({ icon: Icon, title, description, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * index, duration: 0.3 }}
      className="flex items-start gap-3 p-3.5 bg-white/60 border border-slate-200/80 rounded-sm"
      data-testid={`gated-preview-${index}`}
    >
      <div className="p-1.5 bg-[#003366]/[0.06] rounded-sm shrink-0">
        <Icon className="h-3.5 w-3.5 text-[#003366]/60" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold text-[#003366]/80">{title}</span>
          <Lock className="h-2.5 w-2.5 text-slate-300" />
        </div>
        <p className="text-[10px] text-slate-400 leading-snug mt-0.5">{description}</p>
      </div>
    </motion.div>
  );
}

// ──────────────────────────────────────────────────────────
// CANVAS EXPORT HELPERS
// ──────────────────────────────────────────────────────────

async function captureAndDownload(el, opts) {
  const canvas = await html2canvas(el, {
    backgroundColor: '#ffffff', scale: 2, useCORS: true, logging: false, windowWidth: el.scrollWidth,
  });
  const s = 2;
  const hH = opts.headerHeight * s;
  const fH = opts.footerHeight * s;
  const out = document.createElement('canvas');
  out.width = canvas.width;
  out.height = canvas.height + hH + fH;
  const ctx = out.getContext('2d');

  // Header
  ctx.fillStyle = '#003366';
  ctx.fillRect(0, 0, out.width, hH);
  ctx.fillStyle = '#008080';
  ctx.fillRect(0, hH - 3 * s, out.width, 3 * s);
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${opts.titleSize * s}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  ctx.fillText(opts.headerTitle, 24 * s, opts.titleY * s);
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.font = `${11 * s}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  ctx.fillText(opts.headerSubtitle, 24 * s, opts.subtitleY * s);

  // Content
  ctx.drawImage(canvas, 0, hH);

  // Footer
  const fY = hH + canvas.height;
  ctx.fillStyle = '#f1f5f9';
  ctx.fillRect(0, fY, out.width, fH);
  ctx.fillStyle = '#e2e8f0';
  ctx.fillRect(0, fY, out.width, 1 * s);
  ctx.fillStyle = '#64748b';
  ctx.font = `${10 * s}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  ctx.fillText(opts.footerText, 24 * s, fY + opts.footerTextY * s);

  return new Promise((resolve) => {
    out.toBlob((blob) => resolve(blob), 'image/png', 0.92);
  });
}

// ──────────────────────────────────────────────────────────
// MAIN COMPONENT
// ──────────────────────────────────────────────────────────

export default function PIIFinderResults({ analysis, industry, selections, leadForm, setLeadForm, onSubmitLead, leadSubmitted, leadLoading }) {
  const { categories, flags, blindSpots, touchpoints, visibilityGap, nextSteps, summary } = analysis;
  const { score, impactLevel, impactColor, isSDF } = summary;
  const topFlags = flags.slice(0, 3);
  const totalTouchpoints = Object.values(touchpoints).flat().length;
  const intel = INDUSTRY_INTELLIGENCE[industry];
  const snapshotRef = useRef(null);
  const fullReportRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [downloadingFull, setDownloadingFull] = useState(false);

  const stats = SCORE_BAND.statsFn(categories.length, flags.length, blindSpots.length, totalTouchpoints);
  const CategoriesIcon = SECTION_LABELS.categories.icon;
  const TopImpactsIcon = SECTION_LABELS.topImpacts.icon;
  const GapIcon = SECTION_LABELS.visibilityGap.icon;
  const TouchpointsIcon = SECTION_LABELS.touchpoints.icon;
  const BlindSpotsIcon = SECTION_LABELS.blindSpots.icon;
  const NextStepsIcon = SECTION_LABELS.nextSteps.icon;
  const LeadBadgeIcon = LEAD_GATE.badge.icon;
  const CtaBadgeIcon = RESULTS_CTA.badge.icon;

  const handleShareSnapshot = async () => {
    if (!snapshotRef.current || capturing) return;
    setCapturing(true);
    try {
      const blob = await captureAndDownload(snapshotRef.current, {
        headerHeight: 72, footerHeight: 48, titleSize: 20, titleY: 30, subtitleY: 50, footerTextY: 28,
        headerTitle: SNAPSHOT_CONTENT.headerTitle,
        headerSubtitle: SNAPSHOT_CONTENT.headerSubtitle,
        footerText: SNAPSHOT_CONTENT.footerText,
      });
      if (!blob) { setCapturing(false); return; }
      const file = new File([blob], SNAPSHOT_CONTENT.fileName, { type: 'image/png' });
      if (navigator.share) {
        try {
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: 'My DPDP Impact Snapshot' });
            setCapturing(false);
            return;
          }
        } catch { /* fall through */ }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = SNAPSHOT_CONTENT.fileName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { console.error('Snapshot failed:', err); }
    setCapturing(false);
  };

  const handleDownloadFullSummary = async () => {
    if (!fullReportRef.current || downloadingFull) return;
    setDownloadingFull(true);
    try {
      const companyLine = leadForm.company_name ? `${leadForm.company_name}  \u00B7  ` : '';
      const blob = await captureAndDownload(fullReportRef.current, {
        headerHeight: 80, footerHeight: 56, titleSize: 22, titleY: 32, subtitleY: 54, footerTextY: 30,
        headerTitle: DOWNLOAD_SUMMARY.headerTitle,
        headerSubtitle: `${companyLine}Generated by CX0  \u00B7  codexero.ai`,
        footerText: DOWNLOAD_SUMMARY.footerText,
      });
      if (!blob) { setDownloadingFull(false); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = DOWNLOAD_SUMMARY.fileNameFn(leadForm.company_name);
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { console.error('Full summary download failed:', err); }
    setDownloadingFull(false);
  };

  const isFormValid = leadForm.name && leadForm.email && leadForm.company_name;

  // Helper to render a form field (input or select)
  const renderField = (key) => {
    const field = LEAD_FORM_FIELDS[key];
    const FieldIcon = field.icon;
    if (field.type === 'select') {
      return (
        <div key={key}>
          <Label className="text-[11px] font-semibold text-slate-600 flex items-center gap-1">
            <FieldIcon className="h-3 w-3 text-slate-400" /> {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Select value={leadForm[key]} onValueChange={(val) => setLeadForm(p => ({ ...p, [key]: val }))}>
            <SelectTrigger className="mt-1.5 rounded-sm h-9 text-sm border-slate-200" data-testid={field.testId}>
              <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    return (
      <div key={key}>
        <Label className="text-[11px] font-semibold text-slate-600 flex items-center gap-1">
          <FieldIcon className="h-3 w-3 text-slate-400" /> {field.label} {field.required && <span className="text-red-500">*</span>}
        </Label>
        <Input
          type={field.type || 'text'}
          placeholder={field.placeholder}
          value={leadForm[key]}
          onChange={e => setLeadForm(p => ({ ...p, [key]: e.target.value }))}
          className="mt-1.5 rounded-sm h-9 text-sm border-slate-200 focus:border-[#008080] focus:ring-[#008080]/20"
          data-testid={field.testId}
        />
      </div>
    );
  };

  return (
    <div data-testid="pii-results-container">

      {/* ---- Snapshot capture area ---- */}
      <div ref={snapshotRef}>

      {/* ========== SCORE HERO BAND ========== */}
      <div className="bg-[#003366] text-white px-5 sm:px-8 py-7" data-testid="results-score-band">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-300/60 mb-4">{SCORE_BAND.headerLabel}</div>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="relative shrink-0">
            <svg viewBox="0 0 120 120" className="w-28 h-28">
              <circle cx="60" cy="60" r="50" stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
              <motion.circle cx="60" cy="60" r="50" stroke={impactColor} strokeWidth="10" fill="none"
                strokeLinecap="round" transform="rotate(-90 60 60)"
                initial={{ strokeDasharray: '0 314' }}
                animate={{ strokeDasharray: `${score * 3.14} 314` }}
                transition={{ duration: 1.2, ease: 'easeOut' }} />
              <text x="60" y="55" textAnchor="middle" className="text-2xl font-black" fill="white">{score}</text>
              <text x="60" y="72" textAnchor="middle" className="text-[9px] font-bold" fill="rgba(255,255,255,0.4)">/ 100</text>
            </svg>
          </div>
          <div className="text-center sm:text-left flex-1 min-w-0">
            <div className="flex items-center gap-2.5 justify-center sm:justify-start mb-1.5 flex-wrap">
              <span className="text-xl font-black" style={{ color: impactColor }}>{impactLevel} Impact</span>
              {isSDF && (
                <span className="text-[9px] font-bold bg-red-500/20 text-red-300 border border-red-400/30 px-2 py-0.5 rounded" data-testid="results-sdf-badge">
                  {SCORE_BAND.sdfBadge}
                </span>
              )}
            </div>
            <p className="text-xs text-blue-200/70 leading-relaxed max-w-md">
              Your organisation likely handles <strong className="text-white">{categories.length} categories</strong> of personal data
              with <strong className="text-white">{flags.length} compliance obligations</strong> triggered under DPDP Act 2023.
            </p>
          </div>
          <div className="shrink-0 text-center" data-testid="results-gap-badge">
            <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-blue-300/50 mb-1.5">{SCORE_BAND.visibilityGapLabel}</div>
            <div className="px-5 py-2.5 rounded-sm border-2 font-black text-lg tracking-tight"
              style={{ borderColor: visibilityGap.color, color: visibilityGap.color }}>
              {visibilityGap.level}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-center sm:justify-start gap-2.5 mt-5 pt-5 border-t border-white/10">
          {stats.map((stat, i) => (
            <div key={stat.label} className="bg-white/[0.06] rounded-sm px-3.5 py-1.5 text-[11px] font-bold" data-testid={`results-stat-${i}`}>
              <span className="text-white">{stat.value}</span>
              <span className="text-blue-300/50 ml-1">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ========== INDUSTRY CONTEXT ========== */}
      {intel && (
        <div className="px-5 sm:px-8 py-4 border-b border-slate-100 bg-[#008080]/[0.03]" data-testid="results-industry-context">
          <div className="flex items-start gap-2.5">
            <Lightbulb className="h-3.5 w-3.5 text-[#008080] shrink-0 mt-0.5" />
            <div>
              <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#008080]">{intel.shortLabel} DPDP Context</span>
              <div className="space-y-1 mt-1.5">
                {intel.dpdpNotes.slice(0, 2).map((note, i) => (
                  <p key={i} className="text-[11px] text-slate-500 leading-relaxed">{note}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== PERSONAL DATA CATEGORIES ========== */}
      <div className="px-5 sm:px-8 py-6 border-b border-slate-100" data-testid="results-categories-section">
        <SectionLabel icon={CategoriesIcon} label={SECTION_LABELS.categories.labelFn(categories.length)} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {categories.map((cat) => {
            const cs = CONFIDENCE_STYLES[cat.confidence];
            return (
              <div key={cat.id} className={`bg-white border border-slate-200 rounded-sm p-3 ${cs.border}`} data-testid={`result-cat-${cat.id}`}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[13px] font-bold text-[#003366]">{cat.label}</span>
                  <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${cs.badge}`}>{cat.confidence.toUpperCase()}</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-snug">{cat.short}</p>
                {cat.isDirect && (
                  <span className="inline-block mt-1 text-[8px] font-bold text-[#008080] bg-[#008080]/10 px-1.5 py-0.5 rounded">Directly selected</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== TOP 3 DPDP IMPACT AREAS ========== */}
      <div className="px-5 sm:px-8 py-6 border-b border-slate-100" data-testid="results-top-impacts-section">
        <SectionLabel icon={TopImpactsIcon} label={SECTION_LABELS.topImpacts.label} />
        <div className="space-y-2">
          {topFlags.map((flag) => {
            const ss = SEVERITY_STYLES[flag.severity];
            return (
              <div key={flag.id} className={`${ss.bg} border ${ss.border} rounded-sm p-3.5`} data-testid={`result-impact-${flag.id}`}>
                <div className="flex items-center gap-2.5 mb-1">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${ss.dot}`} />
                  <span className={`text-sm font-bold ${ss.text}`}>{flag.label}</span>
                  <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${ss.badge}`}>{flag.severity.toUpperCase()}</span>
                </div>
                <p className="text-[11px] text-slate-500 leading-relaxed ml-[18px]">{flag.description}</p>
                <span className="inline-block mt-1.5 ml-[18px] text-[9px] font-semibold text-slate-400 bg-white px-2 py-0.5 rounded border border-slate-200">{flag.dpdpRef}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== VISIBILITY GAP METER ========== */}
      <div className="px-5 sm:px-8 py-6 border-b border-slate-100" data-testid="results-visibility-gap-section">
        <SectionLabel icon={GapIcon} label={SECTION_LABELS.visibilityGap.label} />
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
          <div className="flex items-center gap-4 mb-3">
            <div className="flex-1">
              <div className="h-3 bg-slate-200 rounded-full overflow-hidden flex">
                {['Low', 'Medium', 'High'].map((level, i) => (
                  <motion.div key={level} className={`h-full ${['bg-green-400', 'bg-amber-400', 'bg-red-400'][i]}`}
                    initial={{ opacity: 0.2 }}
                    animate={{ opacity: visibilityGap.level === level ? 1 : 0.2 }}
                    transition={{ duration: 0.6 }}
                    style={{ width: i < 2 ? '33.3%' : '33.4%' }} />
                ))}
              </div>
              <div className="flex justify-between mt-1">
                {['Low', 'Medium', 'High'].map(level => (
                  <span key={level} className={`text-[9px] font-bold ${visibilityGap.level === level ? (level === 'Low' ? 'text-green-600' : level === 'Medium' ? 'text-amber-600' : 'text-red-600') : 'text-slate-300'}`}>{level}</span>
                ))}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <span className="text-lg font-black" style={{ color: visibilityGap.color }}>{visibilityGap.level}</span>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed">{visibilityGap.description}</p>
        </div>
      </div>

      {/* ---- End snapshot capture area ---- */}
      </div>

      {/* ========== SHARE SNAPSHOT BAR ========== */}
      <div className="px-5 sm:px-8 py-4 border-b border-slate-100 bg-white" data-testid="share-snapshot-bar">
        <button onClick={handleShareSnapshot} disabled={capturing}
          className="w-full flex items-center justify-center gap-2.5 py-3 px-4 bg-[#003366]/[0.03] hover:bg-[#003366]/[0.07] border border-[#003366]/15 rounded-sm transition-all group cursor-pointer"
          data-testid="share-snapshot-btn">
          {capturing ? <Loader2 className="h-4 w-4 animate-spin text-[#003366]" /> : <Lock className="h-4 w-4 text-[#003366] group-hover:scale-110 transition-transform" />}
          <span className="text-sm font-bold text-[#003366]">{capturing ? SNAPSHOT_CONTENT.loadingLabel : SNAPSHOT_CONTENT.buttonLabel}</span>
          <span className="text-[10px] text-slate-400 hidden sm:inline ml-1">{SNAPSHOT_CONTENT.helperText}</span>
        </button>
      </div>

      {/* ========== LEAD CAPTURE GATE / UNLOCKED CONTENT ========== */}
      {!leadSubmitted ? (
        <div className="px-5 sm:px-8 py-8" data-testid="results-lead-gate">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#008080]/[0.06] rounded-full mb-3">
              <LeadBadgeIcon className="h-3 w-3 text-[#008080]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#008080]">{LEAD_GATE.badge.label}</span>
            </div>
            <h3 className="text-lg font-bold text-[#003366] mb-1.5" data-testid="lead-gate-headline">{LEAD_GATE.headline}</h3>
            <p className="text-sm text-slate-500 max-w-lg mx-auto leading-relaxed">{LEAD_GATE.description}</p>
          </div>

          {/* Gated preview cards */}
          <div className="grid sm:grid-cols-3 gap-2.5 mb-6">
            {LEAD_GATE.previewCards.map((card, i) => (
              <GatedPreviewCard key={card.title} icon={card.icon} title={card.title}
                description={card.descriptionFn ? card.descriptionFn(blindSpots.length) : card.description} index={i} />
            ))}
          </div>

          {/* Lead form */}
          <div className="border border-slate-200 rounded-sm overflow-hidden bg-white" data-testid="lead-capture-form">
            <div className="bg-slate-50/80 border-b border-slate-100 px-5 py-3.5">
              <div className="flex items-center gap-2">
                {(() => { const I = LEAD_GATE.form.sectionIcon; return <I className="h-4 w-4 text-[#003366]/60" />; })()}
                <span className="text-[12px] font-bold text-[#003366]">{LEAD_GATE.form.sectionTitle}</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5 ml-6">{LEAD_GATE.form.sectionDescription}</p>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {renderField('name')}
                {renderField('email')}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {renderField('company_name')}
                {renderField('company_size')}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {renderField('role')}
                <div className="flex items-end">
                  <div className="flex items-start gap-2 p-2.5 bg-[#003366]/[0.02] border border-[#003366]/[0.06] rounded-sm w-full">
                    <Lock className="h-3.5 w-3.5 text-[#003366]/40 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-slate-400 leading-snug">{LEAD_GATE.form.privacyNote}</p>
                  </div>
                </div>
              </div>
              <Button onClick={onSubmitLead} disabled={leadLoading || !isFormValid}
                className="w-full bg-[#008080] hover:bg-[#006666] text-white rounded-sm py-5 font-bold text-sm transition-all hover:shadow-lg disabled:opacity-50"
                data-testid="lead-submit-btn">
                {leadLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <>{(() => { const I = RESULTS_CTA.badge.icon; return <I className="h-4 w-4 mr-2" />; })()}</>}
                {LEAD_GATE.form.submitLabel}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>

          {/* ========== EXPANDED SUMMARY BLOCK ========== */}
          <div ref={fullReportRef}>
          <div className="bg-[#003366]/[0.02] border-t border-b border-slate-100 px-5 sm:px-8 py-6" data-testid="expanded-summary-block">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 bg-[#008080] rounded-sm">
                {(() => { const I = SECTION_LABELS.nextSteps.icon; return <I className="h-3.5 w-3.5 text-white" />; })()}
              </div>
              <div>
                <span className="text-[13px] font-bold text-[#003366]">
                  {leadForm.company_name ? `${leadForm.company_name} \u2014 ` : ''}DPDP Impact Summary
                </span>
                <span className="text-[10px] text-slate-400 ml-2">Prepared for {leadForm.name}</span>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { value: score, style: { color: impactColor } },
                { value: categories.length, style: { color: '#003366' } },
                { value: flags.length, style: { color: '#003366' } },
                { value: visibilityGap.level, style: { color: visibilityGap.color } },
              ].map((card, i) => (
                <div key={SUMMARY_CARDS.labels[i].key} className="bg-white border border-slate-200 rounded-sm p-3.5 text-center" data-testid={`summary-card-${SUMMARY_CARDS.labels[i].key}`}>
                  <div className="text-2xl font-black" style={card.style}>{card.value}</div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">{SUMMARY_CARDS.labels[i].label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ========== BUSINESS TOUCHPOINTS ========== */}
          <div className="px-5 sm:px-8 py-6 border-b border-slate-100" data-testid="results-touchpoints-section">
            <SectionLabel icon={TouchpointsIcon} label={SECTION_LABELS.touchpoints.label} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              {['collect', 'store', 'use', 'share'].map(phase => {
                const meta = TOUCHPOINT_META[phase];
                const Icon = meta.icon;
                const items = touchpoints[phase] || [];
                return (
                  <div key={phase} className={`${meta.bg} border ${meta.border} rounded-sm p-3`} data-testid={`touchpoint-${phase}`}>
                    <div className="flex items-center gap-1.5 mb-2.5">
                      <div className={`p-1 rounded-sm ${meta.iconBg}`}><Icon className="h-3 w-3" /></div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.text}`}>{meta.label}</span>
                    </div>
                    <div className="space-y-1.5">
                      {items.length > 0 ? items.map((item, i) => (
                        <div key={i} className="flex items-start gap-1.5">
                          <CircleDot className={`h-2.5 w-2.5 shrink-0 mt-[3px] opacity-40 ${meta.text}`} />
                          <span className="text-[10px] text-slate-600 leading-snug">{item}</span>
                        </div>
                      )) : <span className="text-[10px] text-slate-400 italic">None identified</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ========== BLIND SPOTS ========== */}
          {blindSpots.length > 0 && (
            <div className="px-5 sm:px-8 py-6 border-b border-slate-100" data-testid="results-blindspots-section">
              <SectionLabel icon={BlindSpotsIcon} label={SECTION_LABELS.blindSpots.labelFn(blindSpots.length)} />
              <div className="space-y-2">
                {blindSpots.map((spot, i) => {
                  const ss = SEVERITY_STYLES[spot.severity];
                  return (
                    <div key={spot.id} className={`flex items-start gap-2.5 p-3 ${ss.bg} border ${ss.border} rounded-sm`} data-testid={`result-blindspot-${i}`}>
                      <BlindSpotsIcon className={`h-3.5 w-3.5 ${ss.text} shrink-0 mt-0.5`} />
                      <div>
                        <p className={`text-[11px] ${ss.text} leading-relaxed font-medium`}>{spot.text}</p>
                        <span className={`inline-block mt-1 text-[8px] font-bold px-1.5 py-0.5 rounded ${ss.badge}`}>{spot.severity.toUpperCase()} RISK</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ========== WHAT TO DO NEXT ========== */}
          <div className="px-5 sm:px-8 py-6 border-b border-slate-100" data-testid="results-next-steps-section">
            <SectionLabel icon={NextStepsIcon} label={SECTION_LABELS.nextSteps.label} />
            <div className="space-y-2">
              {nextSteps.map((step, i) => {
                const ps = PRIORITY_STYLES[step.priority] || PRIORITY_STYLES.medium;
                return (
                  <div key={step.label} className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-sm" data-testid={`result-nextstep-${i}`}>
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#003366] text-white text-[10px] font-black shrink-0 mt-0.5">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] font-bold text-[#003366]">{step.label}</span>
                        <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${ps}`}>{step.priority.toUpperCase()}</span>
                      </div>
                      <p className="text-[10px] text-slate-400 leading-relaxed mt-0.5">{step.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          </div>

          {/* ========== DOWNLOAD FULL SUMMARY ========== */}
          <div className="px-5 sm:px-8 py-5 border-b border-slate-100 bg-white" data-testid="download-full-summary-bar">
            <button onClick={handleDownloadFullSummary} disabled={downloadingFull}
              className="w-full flex items-center justify-center gap-2.5 py-3.5 px-4 bg-[#003366]/[0.04] hover:bg-[#003366]/[0.08] border border-[#003366]/15 rounded-sm transition-all group cursor-pointer"
              data-testid="download-full-summary-btn">
              {downloadingFull ? <Loader2 className="h-4 w-4 animate-spin text-[#003366]" /> : <Lock className="h-4 w-4 text-[#003366] group-hover:scale-110 transition-transform" />}
              <span className="text-sm font-bold text-[#003366]">{downloadingFull ? DOWNLOAD_SUMMARY.loadingLabel : DOWNLOAD_SUMMARY.buttonLabel}</span>
              <span className="text-[10px] text-slate-400 hidden sm:inline ml-1">{DOWNLOAD_SUMMARY.helperText}</span>
            </button>
          </div>

          {/* ========== CTA: FULL ASSESSMENT ========== */}
          <div className="bg-[#003366] px-5 sm:px-8 py-10 text-center" data-testid="results-cta-section">
            <div className="max-w-lg mx-auto">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 rounded-full mb-4">
                <CtaBadgeIcon className="h-3 w-3 text-[#008080]" />
                <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-blue-200/80">{RESULTS_CTA.badge.label}</span>
              </div>
              <h4 className="text-xl font-bold text-white mb-2">{RESULTS_CTA.headline}</h4>
              <p className="text-[13px] text-blue-200/60 max-w-md mx-auto mb-5 leading-relaxed">
                {RESULTS_CTA.description}{' '}
                <strong className="text-white">{RESULTS_CTA.descriptionBold}</strong>
                {RESULTS_CTA.descriptionEnd}
              </p>
              <p className="text-[11px] text-blue-200/40 mb-6">{RESULTS_CTA.supportLine}</p>
              <Link to={RESULTS_CTA.ctaHref}>
                <Button className="bg-[#008080] hover:bg-[#006666] text-white px-8 py-5 rounded-sm font-bold text-sm shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  data-testid={RESULTS_CTA.ctaTestId}>
                  {RESULTS_CTA.ctaLabel}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* ========== CONFIRMATION ========== */}
          <div className="bg-[#008080]/5 border-t border-[#008080]/20 px-5 sm:px-8 py-4" data-testid="results-lead-confirmed">
            <div className="flex items-center justify-center gap-2 mb-1">
              {(() => { const I = SECTION_LABELS.nextSteps.icon; return <I className="h-4 w-4 text-[#008080]" />; })()}
              <span className="text-xs font-semibold text-[#003366]">{CONFIRMATION.messageFn(leadForm.email)}</span>
            </div>
            <p className="text-[10px] text-slate-400 text-center">{CONFIRMATION.followUp}</p>
          </div>
        </motion.div>
      )}

      {/* ========== WATERMARK FOOTER ========== */}
      <div className="px-5 sm:px-8 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-center gap-2">
        <span className="text-[9px] text-slate-400 font-semibold">{WATERMARK.prefix}</span>
        <img src={LOGO_URL} alt="CX0" className="h-4" />
        <span className="text-[9px] text-slate-300">|</span>
        <span className="text-[9px] text-slate-400 font-semibold">{WATERMARK.suffix}</span>
      </div>
    </div>
  );
}
